import dayjs from "dayjs";
import { Document } from "flexsearch";
import PAYMENT_METHODS from "./dictionaries/paymentMethods.json";
import SALE_TYPES from "./dictionaries/saleTypes.json";
import SRC_TYPES from "./dictionaries/srcTypes.json";
import { capitalize, createDate, isValidDate, toDateOnlyString } from "./utils";

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const AirlineSuggestion = (airline) => ({
  filterType: "airline",
  value: airline.iata,
  content: `${airline.iata} ${airline.name}`,
});

export const CustomerSuggestion = (customer) => ({
  filterType: "customer",
  value: customer.id,
  content: customer.name,
});

export const MonthSuggestion = (month) => {
  const monthObj = createDate(`${month}-${new Date().getFullYear()}`);
  const start = toDateOnlyString(monthObj.startOf("month"));
  const end = toDateOnlyString(monthObj.endOf("month"));
  return {
    content: month,
    filterType: "saleDate",
    value: { start, end },
  };
};

export const SrcSuggestion = (src) => ({
  content: src.name,
  filterType: "src",
  value: src.id,
});

export const PaymentMethodSuggestion = ({ id, name }) => ({
  content: name,
  filterType: "paymentMethod",
  value: id,
});

export const SalesAgentSuggestion = (sa) => ({
  filterType: "sa",
  value: sa.id,
  content: sa.name,
});

export const SaleTypeSuggestion = ({ id, name }) => ({
  content: `${id} ${name}`,
  filterType: "type",
  value: id,
});

export const SupplierSuggestion = (supplier) => ({
  filterType: "sup",
  value: supplier.id,
  content: supplier.name,
});

let suggestionsIndex;
let suggestionsIndexAutoincrement = 0;

export const addToSuggestions = (filterSuggestions) =>
  Promise.all([
    filterSuggestions.map((suggestion) =>
      suggestionsIndex.addAsync({
        ...suggestion,
        id: ++suggestionsIndexAutoincrement,
      }),
    ),
  ]);

export const resetSuggestions = () => {
  suggestionsIndex = new Document({
    document: {
      index: "content",
      store: ["filterType", "value"],
    },
    preset: "score",
    tokenize: "forward",
    worker: true,
  });

  suggestionsIndexAutoincrement = 0;

  addToSuggestions([
    CustomerSuggestion({ id: "__is_null", name: "No-Customer" }),
  ]);

  addToSuggestions(
    [...Object.values(SRC_TYPES), { id: "__is_null", name: "No-Source" }].map(
      SrcSuggestion,
    ),
  );

  addToSuggestions([
    SalesAgentSuggestion({ id: "__is_null", name: "No-Sales Agent" }),
  ]);

  addToSuggestions([
    SupplierSuggestion({ id: "__is_null", name: "No-Supplier" }),
  ]);

  addToSuggestions(months.map(MonthSuggestion));

  addToSuggestions(Object.values(PAYMENT_METHODS).map(PaymentMethodSuggestion));

  addToSuggestions(Object.values(SALE_TYPES).map(SaleTypeSuggestion));
};

const ddMMM = /^([1-9]|[0-2][0-9]|3[0-1])-\w{3,}$/;
const mmmYYYY = /^\w{3}-(2\d{3})$/;

const suggestDates = (terms) =>
  [...new Set(terms)]
    .map((t) =>
      "today".startsWith(t)
        ? toDateOnlyString(dayjs())
        : ddMMM.test(t)
          ? `${t}-${new Date().getFullYear()}`
          : t,
    )
    .filter(isValidDate)
    .flatMap((t) => {
      const dateTerm = createDate(t);
      const start = mmmYYYY.test(t) ? dateTerm.startOf("month") : dateTerm;
      const end = mmmYYYY.test(t) ? dateTerm.endOf("month") : dateTerm;
      return ["saleDate", "flightDates"].map((filterType) => ({
        filterType,
        value: {
          start: toDateOnlyString(start),
          end: toDateOnlyString(end),
        },
      }));
    });

const suggestPnrs = (terms) =>
  [...new Set(terms)]
    .filter((term) => /^[0-9A-Z]{2,8}$/u.test(term))
    .map((value) => ({ filterType: "pnr", value }));

const suggestPax = (phrase) => {
  return phrase.length >= 3 && /^[\p{L}'-/]+(\s[\p{L}'-/]+){0,2}$/u.test(phrase)
    ? [
        {
          filterType: "pax",
          value: phrase.toLowerCase().split(" ").map(capitalize).join(" "),
        },
      ]
    : [];
};

const suggestTickets = (terms) =>
  [...new Set(terms)]
    .filter((tkt) => /^\d{3,13}$/.test(tkt))
    .map((tkt) => ({ filterType: "tkt", value: tkt }));

export const suggest = async (phrase) => {
  if (!phrase.trim().length) {
    return [];
  }

  const terms = phrase.trim().toLowerCase().split(" ");

  const nonDatesPattern = terms.filter((t) => !isValidDate(t)).join(" ");

  return suggestionsIndex
    .searchAsync(nonDatesPattern, { enrich: true })
    .then((results) => [
      ...suggestTickets(terms),
      ...suggestDates(terms),
      ...suggestPnrs([phrase]),
      ...(results.length ? results[0].result.map(({ doc }) => doc) : results),
      ...suggestPax(phrase.trim()),
    ]);
};
